import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import { cloneDeep } from "lodash";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DateInput } from "../../components/DateInput/DateInput";
import {
  DocumentOfReceive,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { addNewDocument } from "../../services/NewVersionService";
import "./InitialWarehouseRepromaterialStatePage.scss";

export interface InitialWarehouseRepromaterialStatePageProps {}

export const InitialWarehouseRepromaterialStatePage = (
  p: InitialWarehouseRepromaterialStatePageProps,
) => {
  const {} = p;

  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [newDocumentOfReceive, setNewDocumentOfReceive] =
    useState<DocumentOfReceive>({
      id: id ?? "",
      status: ReceiveStatus.Finished,
      warehouse: {} as Warehouse,
      warehouseman: "Početno stanje",
      type: undefined,
      date: new Date(),
      time: new Date(),
      brutto: undefined,
      tara: undefined,
      netto: undefined,
      srps: undefined,
      product: {} as Product,
      productWithCode: "",
      repromaterialProducts: [],
      repromaterialProductsJSON: "",
      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "Početno stanje",
      vehicleNumber: "",
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      gluten: undefined,
      cellNumberFloor: undefined,
      hybrid: "",
      dryingCosts: undefined,
      dryWeight: undefined,
      shippingSlip: "",
      calculationMethod: true,
      foreignProduct: false,
      productOwner: {} as Partner,
      note: "",
      finishedDocument: true,
    });

  const [newProduct, setNewProduct] = useState<Product>({
    id: "",
    name: "",
    type: ProductType.RawMaterial,
    code: "",
    category: {} as ProductCategory,
    lot: "",
    amount: "",
    measuringUnit: "",
  });
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [productsDataTable, setProductsDataTable] = useState<Product[]>([]);

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        const [wResp, pcResp, pResp] = await Promise.all([
          getAllWarehouses(),
          getAllProductCategories(),
          getAllProducts(),
        ]);

        [wResp, pcResp, pResp].forEach(ensureSuccess);

        const wh: Warehouse[] = wResp?.data?.result ?? [];
        const pc: ProductCategory[] = pcResp?.data?.result ?? [];
        const pr: Product[] = pResp?.data?.result ?? [];

        setWarehousesApi(wh);
        setProductCategoriesApi(pc);
        setProductsApi(pr);
        setFilteredProducts(
          pr.filter((x) => x.type === ProductType.RawMaterial),
        );
      } catch (e: any) {
        console.error(e);
        //@ts-ignore
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ?? t("Greška pri učitavanju šifarnika"),
          life: 3500,
        });
        setWarehousesApi([]);
        setProductCategoriesApi([]);
        setProductsApi([]);
        setFilteredProducts([]);
      }
    };

    run();
  }, []);

  useEffect(() => {
    //create all products table
    let newProductsDataTable: Product[] = [];
    if (newDocumentOfReceive.repromaterialProducts) {
      const allProducts = newDocumentOfReceive.repromaterialProducts ?? [];
      for (const productItem of allProducts) {
        const product = productsApi.find((item) => item.id === productItem.id);
        newProductsDataTable.push({
          id: product?.id ?? "",
          type: ProductType.RawMaterial,
          name: productsApi.find((item) => item.id === product?.id)
            ?.name as string,
          code: productsApi.find((item) => item.id === product?.id)
            ?.name as string,
          category: product?.category as ProductCategory,
          lot: productItem?.lot ?? "",
          amount: productItem?.amount ?? "",
          measuringUnit: product?.measuringUnit ?? "",
        });
      }
    }
    setProductsDataTable(newProductsDataTable);
  }, [newDocumentOfReceive, productsApi]);

  useEffect(() => {
    if (newProduct.category?.id === undefined) {
      setFilteredProducts(
        productsApi.filter((x) => x.type === ProductType.RawMaterial),
      );
    } else {
      setFilteredProducts(
        productsApi.filter((x) => x.category?.id == newProduct.category?.id),
      );
    }
  }, [newProduct, productsApi]);

  const toast = useRef(null);

  const handleNewDocumentOfReceive = (prop: string, value: any) => {
    setNewDocumentOfReceive((newDocumentOfReceive) => ({
      ...newDocumentOfReceive,
      [prop]: value,
    }));
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
    copyOfNewDocumentOfReceive.warehouse = warehousesApi.find(
      (item) => item.id == value,
    ) as Warehouse;
    setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
  };

  const handleNewProduct = (prop: string, value: any) => {
    setNewProduct((newProduct) => ({
      ...newProduct,
      [prop]: value,
    }));
  };

  const handleCategoryChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.category = productCategoriesApi.find(
      (category) => category.id === value,
    );
    setNewProduct(copyOfNewProduct);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.id = value;
    copyOfNewProduct.name = value;
    copyOfNewProduct.measuringUnit = filteredProducts.find(
      (product) => product.id === value,
    )?.measuringUnit;
    if (newProduct.category?.id === undefined) {
      copyOfNewProduct.category = filteredProducts.find(
        (product) => product.id === value,
      )?.category;
    }
    setNewProduct(copyOfNewProduct);
  };

  const addNewProduct = () => {
    if (newProduct.name === "" || newProduct.amount === "") {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Moraju biti popunjena polja za naziv i količinu proizvoda`),
        life: 3000,
      });
      return;
    }
    const productAdded = newDocumentOfReceive.repromaterialProducts?.find(
      (item) => item.id == newProduct.id,
    );
    if (!productAdded) {
      //add product to allProductList
      let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
      let copyOfNewProduct = cloneDeep(newProduct);
      copyOfNewDocumentOfReceive.repromaterialProducts?.push(copyOfNewProduct);
      setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
    } else {
      //update existing product
      let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
      newDocumentOfReceive.repromaterialProducts?.map((product, index) => {
        if (product.id === newProduct.id)
          //@ts-ignore
          copyOfNewDocumentOfReceive.repromaterialProducts[index] =
            cloneDeep(newProduct);
      });
      setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
    }

    setFilteredProducts(
      productsApi.filter((item) => item.type === ProductType.RawMaterial),
    );

    //reset newProductFields
    setNewProduct({
      id: "",
      name: "",
      type: ProductType.RawMaterial,
      code: "",
      category: {} as ProductCategory,
      lot: "",
      amount: "",
      measuringUnit: "",
    });
  };

  const onSubmit = async () => {
    let correctChanges: boolean = true;
    let error: string = `${t("Ne možete uneti početnu vrednost stanja repromaterijala u magacin")} ${newDocumentOfReceive.warehouse?.name}. ${t("Postoje već unete prijemnice repromaterijala ili ste već uneli početno stanje")}`;
    let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);

    //ispitivanje da li vec postoje prijemnice repromaterijala u magacin za koju se unosi pocetno stanje
    // for (const repromaterialInput of repromaterialInputs) {
    //     if (repromaterialInput.warehouse?.id === newDocumentOfReceive.warehouse?.id) correctChanges = false;
    // };
    //na zahtev klijenta omogucen unos visestrukih pocetnih stanja repromaterijala u jedan magacin

    if (correctChanges) {
      //@ts-ignore
      toast.current.show({
        severity: "success",
        detail: `${t("Uspešno dodato novo početno stanje repromaterijala u magacin")} ${newDocumentOfReceive.warehouse.name}`,
        life: 3000,
      });
      //@ts-ignore
      if (!copyOfNewDocumentOfReceive.id) delete copyOfNewDocumentOfReceive.id;
      copyOfNewDocumentOfReceive.repromaterialProductsJSON = JSON.stringify(
        newDocumentOfReceive.repromaterialProducts ?? [],
      );
      copyOfNewDocumentOfReceive.mercantile = false as any;
      copyOfNewDocumentOfReceive.input = true as any;

      const resp: AxiosResponse = await addNewDocument(
        copyOfNewDocumentOfReceive as any,
      );
      if (!(resp.status === 200 || resp.status === 201)) {
        throw new Error("Dokument nije snimljen");
      }
      navigate(`/dashboard`);
    } else {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: error,
        life: 5000,
      });
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const nameOfProduct = (rowData: any) => {
    return rowData.name;
  };

  const nameOfCategory = (rowData: any) => {
    return rowData.category.name;
  };

  const editActionBodyTemplate = (rowData: any) => {
    return (
      <ButtonInput
        labelText=""
        icon="bi bi-pencil"
        onButtonClick={() => {
          setNewProduct(rowData);
        }}
        addStyle={{
          backgroundColor: "white",
          color: "rgba(21, 115, 71, 1)",
          border: "none",
        }}
      />
    );
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  let addButtonSaveStyle: React.CSSProperties = {};
  addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
  addButtonSaveStyle.border = "none";
  addButtonSaveStyle.margin = "0 .5rem";

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title-left">
              {t("Početno stanje magacina za repromaterijal")}
            </span>
          </div>
        </div>
        <div className="form-body">
          <DropdownInput
            id="warehouse"
            value={newDocumentOfReceive.warehouse.id}
            onValueChange={handleWarehouseChange}
            optionValue="id"
            optionLabel="name"
            options={warehousesApi.filter((item) => item.virtual !== true)}
            filter={false}
            labelText={t("Magacin *")}
            placeholderText={t("Izaberite iz liste")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
          />
          <DateInput
            id="date"
            value={newDocumentOfReceive.date as Date}
            onValueChange={handleNewDocumentOfReceive}
            labelText={`${t("Početno stanje na dan")} *`}
            placeholderText={t("Izaberite datum")}
            icon="bi bi-calendar-check"
            maxDate={new Date()}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
          />
          <DropdownInput
            id="category"
            value={newProduct.category?.id}
            onValueChange={handleCategoryChange}
            optionValue="id"
            optionLabel="name"
            options={productCategoriesApi.filter(
              (x) => x.type == ProductType.RawMaterial,
            )}
            filter={false}
            labelText={t("Tip proizvoda *")}
            placeholderText={t("Izaberite tip proizvoda")}
            control={control}
            setValue={setValue}
            errors={errors}
          />
          <DropdownInput
            id="name"
            value={newProduct?.id}
            onValueChange={handleProductChange}
            optionValue="id"
            optionLabel="name"
            options={filteredProducts}
            filter={true}
            labelText={t("Naziv proizvoda *")}
            placeholderText={t("Izaberite proizvod")}
            control={control}
            setValue={setValue}
            errors={errors}
          />
          <TextInputWithIcon
            id="amount"
            value={newProduct.amount as string}
            onValueChange={handleNewProduct}
            labelText={t("Količina *")}
            icon="bi bi-minecart-loaded"
            iconAlign="right"
            placeholderText={t("Unesite količinu")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{
              pattern: {
                value: /\d+\.?\d*/,
                message: "Please enter a number",
              },
            }}
          />
          <div className="add-product-item">
            <ButtonInput
              labelText={t("Dodaj proizvod")}
              icon="bi bi-plus-square"
              onButtonClick={addNewProduct}
              addStyle={{
                border: "none",
                background: "rgba(21, 115, 71, 1)",
                color: "#ffffff",
                height: "2.6rem",
              }}
            />
          </div>
          <div className="data-table-container">
            <DataTable value={productsDataTable}>
              <Column
                body={nameOfProduct}
                header={t("Naziv proizvoda:")}
              ></Column>
              <Column
                body={nameOfCategory}
                header={t("Tip proizvoda:")}
              ></Column>
              <Column field="amount" header={t("Količina:")}></Column>
              <Column field="measuringUnit" header={t("Merna jed:")}></Column>
              <Column
                body={editActionBodyTemplate}
                className="datatable-button-column"
                header={t("Opcije")}
              />
            </DataTable>
          </div>
          <div className="divider-line">
            <Divider />
          </div>
          <div className="empty-field"></div>
          <div className="footer-buttons">
            <ButtonInput
              labelText={t("Sačuvaj")}
              onButtonClick={handleSubmit(onSubmit, onError)}
              addStyle={addButtonSaveStyle}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
